import * as React from 'react';
import { useContext, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { MobileSlideSurface } from '../../../components/Surface/MobileSlideSurface';
import useOutsideClick from '../../../helpers/hooks/UseOutsideClick';
import { ContextMenu } from '../ContextMenu';
import {
  useClientPoint,
  useFloating,
  useInteractions
} from '@floating-ui/react';
import { makeCSS } from '../../../utils/makeCSS';

const useCSS = makeCSS(({ theme }) => {
  return {
    paper: {
      width: 'max-content' // the width of the menu is sized to its contents.
    }
  };
});

export interface ContextualMenuPopoverUIProps {}

interface ContextMenuContext {
  menuOpen: boolean;
  clientX: number;
  clientY: number;
  handleClose(e: React.MouseEvent): void;
  handleClearAll(e: React.MouseEvent): void;
  onContextMenu?(e: React.MouseEvent): void;
}

export const contextMenuContext = React.createContext<ContextMenuContext>({
  menuOpen: false,
  clientX: 0,
  clientY: 0,
  handleClose: (e) => {},
  handleClearAll: (e) => {},
  onContextMenu: (e) => {}
});

export const ContextualMenuPopoverUI: React.FC<
  React.PropsWithChildren<ContextualMenuPopoverUIProps>
> = (props) => {
  const { children } = props;

  const { menuOpen, clientX, clientY, handleClose, handleClearAll } =
    useContext(contextMenuContext);

  const classes = useCSS();

  const outsideRef = useOutsideClick(handleClose);
  const anchorRef = React.useRef();

  const { refs, floatingStyles, context } = useFloating({
    open: menuOpen
  });

  const clientPoint = useClientPoint(context, {
    enabled: menuOpen,
    x: menuOpen ? clientX : undefined,
    y: menuOpen ? clientY : undefined
  });

  const { getFloatingProps } = useInteractions([clientPoint]);

  //calculate the available viewport height for the scrollbar height
  const availableSpace = window.innerHeight - clientY - 50;
  const dynamicHeight = Math.min(650, availableSpace);

  return (
    <>
      {!isMobile ? (
        <>
          {menuOpen && (
            <div
              ref={outsideRef}
              onClick={(e) => {
                handleClose(e);
                e.stopPropagation();
                e.preventDefault();
              }}
              onContextMenu={(e) => {
                handleClose(e);
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
              >
                <div ref={anchorRef} className={`${classes.paper} `}>
                  <ContextMenu
                    anchor={anchorRef.current}
                    width={'100%'}
                    scrollbarHeight={dynamicHeight}
                    placement="bottom-start"
                    offset={4}
                  >
                    <div style={{ paddingRight: 2 }}>{children}</div>
                  </ContextMenu>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {menuOpen && (
            <div ref={outsideRef}>
              <MobileSlideSurface
                onClose={(e?: any) => {
                  handleClose(e);
                  handleClearAll(e);
                }}
              >
                {children}
              </MobileSlideSurface>
            </div>
          )}
        </>
      )}
    </>
  );
};
